import { t as uiText } from '../i18n';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { useAuthContext } from '../context/AuthContext';
import { updateWorkspaceIcon } from '../features/workspace/updateWorkspaceIcon';
import { updateWorkspaceSettings } from '../features/workspace/updateWorkspaceSettings';
import { invalidateWorkspaceFetchCache } from '../features/workspace/fetchUserWorkspaces';
import { getWorkspaceIconPath, getWorkspaceIconURL, clearWorkspaceIconCache } from '../features/workspace/workspaceIconUtils';
import { useIsWorkspaceAdminQuery } from '../features/auth/queries/workspaceRoleQueries';
import { updateWorkspaceLinkage, WorkspaceLinkType } from '../features/workspace/updateWorkspaceLinkage';
import { useWorkspaceEntitlements } from '../features/plans/useWorkspaceEntitlements';
import ImageCropModal from './ImageCropModal';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import {
  DEFAULT_WORKSPACE_BASE_COLOR_KEY,
  isWorkspaceBaseColorKey,
  WORKSPACE_BASE_COLOR_OPTIONS,
  WORKSPACE_BASE_COLOR_PALETTES,
  type WorkspaceBaseColorKey,
} from '../theme/workspaceBaseColors';

interface WorkspaceSettingsModalProps {
  onClose: () => void;
}

type Company = {
  id: string;
  name: string;
};

const WorkspaceSettingsModal: React.FC<WorkspaceSettingsModalProps> = ({ onClose }) => {
  const { workspaceId: routeWorkspaceId } = useParams();
  const { selectedWorkspace, refresh, workspaces } = useWorkspaceContext();
  const { user } = useAuthContext();
  const { can } = useWorkspaceEntitlements();
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [iconUrl, setIconUrl] = useState<string | null>(null);
  const [iconFile, setIconFile] = useState<File | null>(null);
  /** 「画像を削除」で既存アイコンを消す意図があるときだけ true（名前のみ保存では触れない） */
  const [iconRemovalRequested, setIconRemovalRequested] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [baseColorKey, setBaseColorKey] = useState<WorkspaceBaseColorKey>(
    DEFAULT_WORKSPACE_BASE_COLOR_KEY
  );
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // 紐付け（company / 親ワークスペース）
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [linkType, setLinkType] = useState<WorkspaceLinkType>('none');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedParentWorkspaceId, setSelectedParentWorkspaceId] = useState<string>('');
  const [initialCompanyId, setInitialCompanyId] = useState<string>('');
  const [initialParentWorkspaceId, setInitialParentWorkspaceId] = useState<string>('');

  // 管理者権限チェック
  const { data: isAdmin, isLoading: isAdminLoading } = useIsWorkspaceAdminQuery(
    routeWorkspaceId ?? '', 
    !!routeWorkspaceId
  );

  useEffect(() => {
    const loadWorkspaceData = async () => {
      if (!routeWorkspaceId) return;

      // route を唯一の基準として、ID表示は先に確定させる
      setWorkspaceId(routeWorkspaceId);

      // selectedWorkspace が未解決でも route ID から直接取得を試みる
      if (selectedWorkspace) {
        setWorkspaceName(selectedWorkspace.name || '');
      }

      // 紐付けの初期値（Contextの値 + 念のためworkspaces/{id} を直接参照）
      try {
        const wsSnap = await getDoc(doc(db, 'workspaces', routeWorkspaceId));
        if (wsSnap.exists()) {
          const data = wsSnap.data() as any;
          const rawBaseColorKey =
            data?.baseColorKey || selectedWorkspace?.baseColorKey || DEFAULT_WORKSPACE_BASE_COLOR_KEY;
          setBaseColorKey(
            isWorkspaceBaseColorKey(rawBaseColorKey)
              ? rawBaseColorKey
              : DEFAULT_WORKSPACE_BASE_COLOR_KEY
          );
          setWorkspaceName((data?.name || selectedWorkspace?.name || '') as string);
          const currentCompanyId = (data?.companyId || selectedWorkspace?.companyId || '') as string;
          const currentParentWorkspaceId = (data?.parentWorkspaceId || selectedWorkspace?.parentWorkspaceId || '') as string;
          setInitialCompanyId(currentCompanyId);
          setInitialParentWorkspaceId(currentParentWorkspaceId);

          if (currentParentWorkspaceId) {
            setLinkType('workspace');
            setSelectedParentWorkspaceId(currentParentWorkspaceId);
            setSelectedCompanyId(''); // companyId は親から継承するためUIでは未選択扱い
          } else if (currentCompanyId) {
            setLinkType('company');
            setSelectedCompanyId(currentCompanyId);
            setSelectedParentWorkspaceId('');
          } else {
            setLinkType('none');
            setSelectedCompanyId('');
            setSelectedParentWorkspaceId('');
          }
        } else {
          // 取得できない場合はContextの値をフォールバック
          const rawBaseColorKey =
            selectedWorkspace?.baseColorKey || DEFAULT_WORKSPACE_BASE_COLOR_KEY;
          setBaseColorKey(
            isWorkspaceBaseColorKey(rawBaseColorKey)
              ? rawBaseColorKey
              : DEFAULT_WORKSPACE_BASE_COLOR_KEY
          );
          setWorkspaceName(selectedWorkspace?.name || '');
          const currentCompanyId = (selectedWorkspace?.companyId || '') as string;
          const currentParentWorkspaceId = (selectedWorkspace?.parentWorkspaceId || '') as string;
          setInitialCompanyId(currentCompanyId);
          setInitialParentWorkspaceId(currentParentWorkspaceId);
          if (currentParentWorkspaceId) {
            setLinkType('workspace');
            setSelectedParentWorkspaceId(currentParentWorkspaceId);
            setSelectedCompanyId('');
          } else if (currentCompanyId) {
            setLinkType('company');
            setSelectedCompanyId(currentCompanyId);
            setSelectedParentWorkspaceId('');
          } else {
            setLinkType('none');
            setSelectedCompanyId('');
            setSelectedParentWorkspaceId('');
          }
        }
      } catch (error) {
        console.error('❌ ワークスペース紐付け情報読み込みエラー:', error);
      }

      // アイコンURLを取得
      try {
        const iconUrl = await getWorkspaceIconURL(routeWorkspaceId);
        setIconUrl(iconUrl);
      } catch (error) {
        console.error('❌ ワークスペースアイコン読み込みエラー:', error);
        setIconUrl(null);
      }
      
      setIconFile(null); // ファイル選択をリセット
      setIconRemovalRequested(false);
      setUploadProgress(0);
      setShowCropModal(false);
      setSelectedImageFile(null);
      
      // OGメタデータの状態はこの画面では扱わない
    };
    
    loadWorkspaceData();
  }, [selectedWorkspace, routeWorkspaceId]);

  // 企業一覧を取得（users/{uid}/companies → companies/{companyId}）
  useEffect(() => {
    const loadCompanies = async () => {
      if (!user?.uid) return;
      setLoadingCompanies(true);
      try {
        const userCompaniesRef = collection(db, `users/${user.uid}/companies`);
        const snap = await getDocs(userCompaniesRef);
        const companyIds = snap.docs.map(d => d.id);
        const companyDocs = await Promise.all(
          companyIds.map(async (cid) => {
            try {
              // companies/{companyId}/members/{uid} を読める = 少なくとも会社メンバー（ルールで保護）
              // read が拒否された companyId は候補から除外して、後段の permission-denied を予防する
              try {
                const myMemberSnap = await getDoc(doc(db, 'companies', cid, 'members', user.uid));
                if (!myMemberSnap.exists()) return null;
                const myMember = myMemberSnap.data() as any;
                if (myMember?.status !== 'active') return null;
              } catch (e) {
                return null;
              }

              const cSnap = await getDoc(doc(db, 'companies', cid));
              if (!cSnap.exists()) return null;
              const data = cSnap.data() as any;
              return { id: cid, name: (data?.name || cid) as string };
            } catch (e) {
              console.error(`Failed to load company ${cid}:`, e);
              return null;
            }
          })
        );
        setCompanies(companyDocs.filter(Boolean) as Company[]);
      } catch (error) {
        console.error('Failed to load companies:', error);
        setCompanies([]);
      } finally {
        setLoadingCompanies(false);
      }
    };
    void loadCompanies();
  }, [user?.uid]);

  const parentWorkspaceCandidates = (workspaces || [])
    .filter(w => !!w?.id)
    .filter(w => w.id !== routeWorkspaceId)
    // 親にできるのは「子ではないワークスペース」のみに限定（Create時と同じ）
    .filter(w => !w.parentWorkspaceId);
  const requiresCompanySelection =
    isAdmin && linkType === 'company' && !selectedCompanyId;
  const requiresParentWorkspaceSelection =
    isAdmin && linkType === 'workspace' && !selectedParentWorkspaceId;
  const isSubmitDisabled =
    isLoading ||
    requiresCompanySelection ||
    requiresParentWorkspaceSelection;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!workspaceName.trim()) {
      alert(uiText("Please enter a workspace name"));
      return;
    }

    if (!routeWorkspaceId) {
      alert(uiText("Workspace ID not found"));
      return;
    }

    setIsLoading(true);
    
    try {
      // 紐付け更新（管理者のみ）
      if (isAdmin) {
        const linkageChanged =
          linkType === 'none'
            ? Boolean(initialCompanyId || initialParentWorkspaceId)
            : linkType === 'company'
              ? (initialParentWorkspaceId || '') !== '' || (initialCompanyId || '') !== (selectedCompanyId || '')
              : (initialParentWorkspaceId || '') !== (selectedParentWorkspaceId || '');

        if (linkageChanged) {
          if (linkType === 'none') {
            await updateWorkspaceLinkage({
              workspaceId: routeWorkspaceId,
              linkType: 'none',
            });
          } else if (linkType === 'company') {
            if (!selectedCompanyId) {
              alert(uiText("Please select an organization"));
              return;
            }
            await updateWorkspaceLinkage({
              workspaceId: routeWorkspaceId,
              linkType: 'company',
              companyId: selectedCompanyId,
            });
          } else {
            if (!selectedParentWorkspaceId) {
              alert(uiText("Select a parent workspace"));
              return;
            }
            if (selectedParentWorkspaceId === routeWorkspaceId) {
              alert(uiText("A workspace cannot be its own parent"));
              return;
            }
            await updateWorkspaceLinkage({
              workspaceId: routeWorkspaceId,
              linkType: 'workspace',
              parentWorkspaceId: selectedParentWorkspaceId,
            });
          }
        }
      }

      // ワークスペース設定を更新
      await updateWorkspaceSettings({
        workspaceId: routeWorkspaceId,
        name: workspaceName,
        baseColorKey,
      });

      // 保存後の視覚反映を即時化（refresh完了前に表示色を合わせる）
      const palette = WORKSPACE_BASE_COLOR_PALETTES[baseColorKey];
      const root = document.documentElement;
      root.style.setProperty('--workspace-primary', palette.primary);
      root.style.setProperty('--workspace-primary-dark', palette.primaryDark);
      root.style.setProperty('--workspace-bg-soft', palette.bgSoft);
      root.style.setProperty('--workspace-border-soft', palette.borderSoft);
      root.style.setProperty('--workspace-text-on-primary', palette.textOnPrimary);
      root.style.setProperty('--beige-primary', palette.primary);
      root.style.setProperty('--beige-primary-dark', palette.primaryDark);
      root.style.setProperty('--beige-soft-bg', palette.bgSoft);

      // 30秒キャッシュで反映が遅れないように明示無効化
      if (user?.uid) {
        invalidateWorkspaceFetchCache(user.uid);
      }

      // アイコンを更新（新規アップロード／差し替え、または「画像を削除」時のみ。プレビュー表示だけでは走らせない）
      if (iconFile || iconRemovalRequested) {
        const currentIconPath = await getWorkspaceIconPath(routeWorkspaceId);

        await updateWorkspaceIcon({
          workspaceId: routeWorkspaceId,
          imageFile: iconFile,
          currentIconPath: currentIconPath,
          onProgress: (progress) => {
            setUploadProgress(progress);
          }
        });

        // アイコンURLのキャッシュを明示的にクリアして即時反映させる
        clearWorkspaceIconCache(routeWorkspaceId);
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error('ワークスペース設定の更新に失敗:', error);
      alert(uiText("Failed to update workspace settings"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          padding: '32px',
          width: '90%',
          maxWidth: '640px',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{
          margin: '0 0 24px 0',
          fontSize: '20px',
          fontWeight: 'bold',
          color: '#333',
          textAlign: 'center'
        }}>
          {uiText("Workspace settings")}
        </h2>

        <form onSubmit={handleSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
          {/* 紐付け設定（管理者のみ。プランが workspace.linkage を持つ場合のみ） */}
          {can('workspace.linkage') && (
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {uiText("Linking settings")}
            </label>

            {!isAdminLoading && !isAdmin && (
              <div style={{ padding: '10px 12px', backgroundColor: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
                {uiText("Only admins can change the link")}
              </div>
            )}

            {isAdmin && (
              <div style={{ padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '4px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#333' }}>
                    <input
                      type="radio"
                      name="linkType"
                      checked={linkType === 'none'}
                      onChange={() => {
                        setLinkType('none');
                        setSelectedCompanyId('');
                        setSelectedParentWorkspaceId('');
                      }}
                      disabled={isLoading}
                    />
                    {uiText("No link")}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#333' }}>
                    <input
                      type="radio"
                      name="linkType"
                      checked={linkType === 'company'}
                      onChange={() => {
                        setLinkType('company');
                        setSelectedParentWorkspaceId('');
                        if (!selectedCompanyId) setSelectedCompanyId(initialCompanyId);
                      }}
                      disabled={isLoading}
                    />
                    {uiText("Associate with an organization")}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#333' }}>
                    <input
                      type="radio"
                      name="linkType"
                      checked={linkType === 'workspace'}
                      onChange={() => {
                        setLinkType('workspace');
                        setSelectedCompanyId('');
                        if (!selectedParentWorkspaceId) setSelectedParentWorkspaceId(initialParentWorkspaceId);
                      }}
                      disabled={isLoading}
                    />
                    {uiText("Link to a parent workspace")}
                  </label>
                </div>

                {linkType === 'none' ? (
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                      {uiText("Current:")} {initialCompanyId
                        ? `company=${initialCompanyId}`
                        : initialParentWorkspaceId
                          ? `parent=${initialParentWorkspaceId}`
                          : uiText("Not set")}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {uiText("Do not associate with an organization or a parent workspace")}
                    </div>
                  </div>
                ) : linkType === 'company' ? (
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                      {uiText("Current:")} {initialCompanyId ? `company=${initialCompanyId}` : uiText("Not set")}
                    </div>
                    <select
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                      required={linkType === 'company'}
                      disabled={isLoading || loadingCompanies}
                      aria-invalid={requiresCompanySelection}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: requiresCompanySelection ? '1px solid #dc3545' : '1px solid #ccc',
                        backgroundColor: '#fff',
                        fontSize: '14px',
                      }}
                    >
                      <option value="">{loadingCompanies ? uiText("Loading now...") : uiText("Please select")}</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                      {uiText("This also updates the company's workspace list")}
                    </div>
                    {requiresCompanySelection && (
                      <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '6px' }}>
                        {uiText("Please select an organization")}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>
                      {uiText("Current:")} {initialParentWorkspaceId ? `parent=${initialParentWorkspaceId}` : uiText("Not set")}
                    </div>
                    <select
                      value={selectedParentWorkspaceId}
                      onChange={(e) => setSelectedParentWorkspaceId(e.target.value)}
                      required={linkType === 'workspace'}
                      disabled={isLoading}
                      aria-invalid={requiresParentWorkspaceSelection}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: requiresParentWorkspaceSelection ? '1px solid #dc3545' : '1px solid #ccc',
                        backgroundColor: '#fff',
                        fontSize: '14px',
                      }}
                    >
                      <option value="">{uiText("Please select")}</option>
                      {parentWorkspaceCandidates.map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name || w.id}
                        </option>
                      ))}
                    </select>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                      {uiText("Linking to a parent workspace inherits the company ID from the parent workspace")}
                    </div>
                    {requiresParentWorkspaceSelection && (
                      <div style={{ fontSize: '12px', color: '#dc3545', marginTop: '6px' }}>
                        {uiText("Select a parent workspace")}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* アイコン設定 */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {uiText("Icon settings *")}
            </label>
            
            {/* アイコン表示エリア */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              marginBottom: '12px'
            }}>
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '6px',
                  border: '2px solid var(--workspace-primary, #cfae91)',
                  backgroundColor: 'var(--workspace-border-soft, #f5f0eb)',
                  color: 'var(--workspace-primary, #cfae91)',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  overflow: 'hidden'
                }}
              >
                {iconUrl ? (
                  <img
                    src={iconUrl}
                    alt={uiText("Workspace icon")}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const textElement = e.currentTarget.nextElementSibling as HTMLElement;
                      if (textElement) {
                        textElement.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: iconUrl ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: 'var(--workspace-primary, #cfae91)'
                  }}
                >
                  {(workspaceName || selectedWorkspace?.name || '').charAt(0) || 'A'}
                </div>
              </div>
              
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '4px' }}>
                  {uiText("Preview")}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {iconUrl ? uiText("Image icon") : uiText("Default icon")}
                </div>
              </div>
            </div>

            {/* 画像アップロード */}
            <div style={{ marginBottom: '8px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#333'
              }}>
                {uiText("Image icon")}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/*';
                    input.onchange = (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        setSelectedImageFile(file);
                        setShowCropModal(true);
                      }
                    };
                    input.click();
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: 'var(--workspace-primary, #cfae91)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  {uiText("Select file")}
                </button>
                <div
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '14px',
                    color: '#666',
                  }}
                >
                  {iconFile ? iconFile.name : uiText("None selected")}
                </div>
              </div>
              {(iconUrl || iconFile) && (
                <button
                  type="button"
                  onClick={() => {
                    setIconUrl(null);
                    setIconFile(null);
                    setSelectedImageFile(null);
                    setIconRemovalRequested(true);
                  }}
                  style={{
                    marginTop: '8px',
                    padding: '4px 8px',
                    backgroundColor: '#ff6b6b',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  {uiText("Delete image")}
                </button>
              )}
            </div>
            
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              {uiText("Select an image to display as the workspace icon")}
            </div>
          </div>

          {/* ワークスペース名 */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {uiText("Base color")}
            </label>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '8px'
            }}>
              {WORKSPACE_BASE_COLOR_OPTIONS.map((option) => {
                const palette = WORKSPACE_BASE_COLOR_PALETTES[option.key];
                const selected = baseColorKey === option.key;
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setBaseColorKey(option.key)}
                    disabled={isLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      border: selected ? `2px solid ${palette.primary}` : '1px solid #ddd',
                      borderRadius: '6px',
                      backgroundColor: selected ? palette.borderSoft : '#fff',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      opacity: isLoading ? 0.7 : 1,
                    }}
                  >
                    <span style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: palette.primary,
                      border: `1px solid ${palette.primaryDark}`,
                      flexShrink: 0,
                    }} />
                    <span style={{ fontSize: '13px', color: '#333', fontWeight: selected ? 'bold' : 'normal' }}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '6px'
            }}>
            </div>
          </div>

          {/* ワークスペース名 */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {uiText("Workspace name *")}
            </label>
            <input
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder={uiText("Example: Sales team")}
              maxLength={48}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'var(--workspace-primary, #cfae91)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
              }}
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px',
              textAlign: 'right'
            }}>
              {workspaceName.length}/48
            </div>
          </div>

          {/* ワークスペースID */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {uiText("Workspace ID *")}
            </label>
            <input
              type="text"
              value={workspaceId}
              readOnly
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                boxSizing: 'border-box',
                outline: 'none',
                backgroundColor: '#f5f5f5',
                color: '#666',
                cursor: 'not-allowed'
              }}
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              {uiText("The workspace ID cannot be changed")}
            </div>
          </div>

          {/* ボタン */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}>
            <button
              type="submit"
              disabled={isSubmitDisabled}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: isSubmitDisabled ? '#ccc' : 'var(--workspace-primary, #cfae91)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                transition: isLoading ? 'none' : 'background-color 0.2s',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (!isSubmitDisabled) {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSubmitDisabled) {
                  e.currentTarget.style.backgroundColor = 'var(--workspace-primary, #cfae91)';
                }
              }}
            >
              {isLoading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span>{uiText("Updating...")} {uploadProgress}%</span>
                  {uploadProgress > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        height: '3px',
                        backgroundColor: 'var(--workspace-primary, #cfae91)',
                        width: `${uploadProgress}%`,
                        transition: 'width 0.3s ease'
                      }}
                    />
                  )}
                </div>
              ) : (
                uiText("Update")
              )}
            </button>
            {requiresCompanySelection && (
              <div style={{
                fontSize: '12px',
                color: '#dc3545',
                textAlign: 'center'
              }}>
                {uiText("To associate with an organization, you must select one")}
              </div>
            )}
            {requiresParentWorkspaceSelection && (
              <div style={{
                fontSize: '12px',
                color: '#dc3545',
                textAlign: 'center'
              }}>
                {uiText("Select a parent workspace to link one")}
              </div>
            )}
            <button
              type="button"
              onClick={handleBack}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: 'transparent',
                color: '#666',
                border: 'none',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {uiText("←Back")}
            </button>
          </div>
        </form>
      </div>
      {/* 更新完了モーダル */}
      {showSuccessModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
          }}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              padding: '32px 40px',
              maxWidth: '360px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              textAlign: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>✓</div>
            <p style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', margin: '0 0 24px 0' }}>
              {uiText("Workspace settings updated")}
            </p>
            <button
              onClick={async () => {
                setShowSuccessModal(false);
                await refresh();
                onClose();
              }}
              style={{
                padding: '10px 32px',
                backgroundColor: 'var(--workspace-primary, #cfae91)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* 画像クロップモーダル */}
      {showCropModal && selectedImageFile && (
        <ImageCropModal
          imageFile={selectedImageFile}
          cropType="square"
          onCropComplete={(croppedFile, dataUrl) => {
            setIconFile(croppedFile);
            setIconUrl(dataUrl);
            setIconRemovalRequested(false);
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
          onCancel={() => {
            setShowCropModal(false);
            setSelectedImageFile(null);
          }}
        />
      )}
    </div>
  );
};

export default WorkspaceSettingsModal; 